"use client";

import { useMemo } from "react";
import useAddToBasket from "./useAddToBasket";
import useCollectionScroll from "./useCollectionScroll";
import useFilterResultsScroll from "./useFilterResultsScroll";
import useDailyCompetitions from "./useDailyCompetitions";
import useLifestyleCollections from "./useLifestyleCollections";
import useLifestyleFilters from "./useLifestyleFilters";
import useLifestylePrizes from "./useLifestylePrizes";
import useLifestyleUrlState, { buildSearch } from "./useLifestyleUrlState";
import useProgressiveReveal from "./useProgressiveReveal";
import useSyncHeaderHeight from "@/app/hooks/useSyncHeaderHeight";

/**
 * Composition root for /prizes/lifestyle.
 *
 * Wires the data, date, filter, collection, URL and basket hooks together so
 * `page.tsx` stays a thin renderer. Each hook underneath is independently
 * testable and none of them touch the DOM beyond the History API.
 */
export default function useLifestylePage() {
  useSyncHeaderHeight();

  const { prizes, multibuy, averageRrp, isLoading, error, competitionId, endDateTime } = useLifestylePrizes();

  const daily = useDailyCompetitions(prizes);
  const filters = useLifestyleFilters(prizes, daily.matchesSelectedDate);

  const collections = useLifestyleCollections({
    prizes,
    competitionId,
    isDirty: filters.isDirty,
  });

  // Collections are jump links rather than a filter, so the modal's
  // Collections tab scrolls to a row instead of narrowing the results.
  const scrollToCollection = useCollectionScroll(collections.isFiltering, filters.clear);

  useLifestyleUrlState({
    filters: filters.filters,
    selectedDate: daily.selectedDate,
    hydrate: filters.hydrate,
    setSelectedDate: daily.setSelectedDate,
  });

  /** What the flat ALL PRIZES grid shows. */
  const gridPrizes = filters.filteredPrizes;

  // Changing any filter is a new result set, so the reveal starts over.
  const revealKey = useMemo(
    () => buildSearch(filters.filters, daily.selectedDate),
    [filters.filters, daily.selectedDate]
  );

  useFilterResultsScroll(filters.isDirty, revealKey);

  const reveal = useProgressiveReveal(gridPrizes.length, revealKey);

  const visiblePrizes = useMemo(() => gridPrizes.slice(0, reveal.revealed), [gridPrizes, reveal.revealed]);

  const basket = useAddToBasket({ competitionIdsForPrize: daily.competitionIdsForPrize });

  return {
    // Data
    prizes,
    multibuy,
    averageRrp,
    competitionId,
    competitionEndDateTime: endDateTime,
    isLoading: isLoading || collections.isLoading,
    error,

    // Presentation
    heroPrizes: collections.heroPrizes,
    collections: collections.collections,
    /** True once filtering starts; the page swaps rows for the flat grid. */
    isFiltering: collections.isFiltering,
    gridPrizes,
    visiblePrizes,
    reveal,

    // Controls
    filters,
    daily,
    basket,
    scrollToCollection,
  };
}
